import { Box, Grid, Typography } from '@mui/material'
import { FastField } from 'formik'
import React from 'react'
import { CustomCheckbox, CustomRadio, CustomRadioGroup, CustomTextField } from 'src/form'
import BillingCodes from './intakeNote/BillingCodes'

const PHYSICAL_APPEARANCE_OPTIONS = [
  { value: 'normal', label: 'All findings are within normal limits' },
  { value: 'abnormal', label: 'One or more findings are outside normal limits' },
]

const Section: React.FC<{ title: string; mb?: number }> = (props) => (
  <Grid item xs={12} mt={4} mb={props.mb}>
    <Typography variant="h3">{props.title}</Typography>
    {props.children}
  </Grid>
)

type IntakeNotePsychiatryFieldsProps = {
  viewOnly?: boolean
}

const IntakeNotePsychiatryFields = ({ viewOnly }: IntakeNotePsychiatryFieldsProps) => (
  <>
    <Section title="General intake information">
      <FastField
        fullWidth
        component={CustomCheckbox}
        name="visitTypeRemote"
        label="Visit Type: Synchronous audio-video"
      />
      <FastField
        component={CustomTextField}
        label="Chief Concern"
        helperText=""
        fullWidth
        multiline
        name="chiefConcern"
        minRows={1}
      />
      <FastField
        component={CustomTextField}
        label="Current Medications, Supplements, and Vitamins"
        helperText="If none, enter NA"
        fullWidth
        multiline
        name="currentMediciationsSupplementsAndVitamins"
        minRows={4}
      />
      <FastField
        component={CustomTextField}
        label="Past Medications and Treatments"
        helperText="If none, enter NA"
        fullWidth
        multiline
        name="pastMedications"
        minRows={4}
      />
      <FastField
        component={CustomTextField}
        label="Side Effects of Medications and Treatments"
        helperText="If none, enter NA"
        fullWidth
        multiline
        name="sideEffects"
        minRows={4}
      />
      <FastField
        component={CustomTextField}
        label="Allergies"
        helperText="If none, enter NA"
        fullWidth
        multiline
        name="allergies"
        minRows={4}
      />
      <Box mt={3} mx={0}>
        <Typography variant="h4">Physical appearance</Typography>
        <Typography variant="body1">
          No acute distress, speech fluent. Respiratory rate appears within normal limits.
        </Typography>
        <FastField component={CustomRadioGroup} name="physicalAppearance">
          {PHYSICAL_APPEARANCE_OPTIONS.map((visitType) => (
            <CustomRadio key={visitType.value} label={visitType.label} value={visitType.value} />
          ))}
        </FastField>
      </Box>
      <FastField component={CustomTextField} label="HPI" helperText="" fullWidth multiline name="hpi" minRows={4} />
    </Section>

    <Section title="Psychiatric History">
      <FastField
        component={CustomTextField}
        label="Trauma History"
        helperText=""
        fullWidth
        multiline
        name="psychiatricHistory.traumaHistory"
        minRows={4}
      />
      <FastField
        component={CustomTextField}
        label="Post-Traumatic Stress Symptoms"
        helperText=""
        fullWidth
        multiline
        name="psychiatricHistory.postTraumaticStressSymptoms"
        minRows={4}
      />
      <FastField
        component={CustomTextField}
        label="Anxiety Symptoms"
        helperText=""
        fullWidth
        multiline
        name="psychiatricHistory.anxietySymptoms"
        minRows={4}
      />
      <FastField
        component={CustomTextField}
        label="Mood Symptoms"
        helperText=""
        fullWidth
        multiline
        name="psychiatricHistory.moodSymptoms"
        minRows={4}
      />
      <FastField
        component={CustomTextField}
        label="Depression and Manic Symptoms"
        helperText=""
        fullWidth
        multiline
        name="psychiatricHistory.depressionAndManicSymptoms"
        minRows={4}
      />
      <FastField
        component={CustomTextField}
        label="Lethality Risk (Self-Harm or Harm to Others)"
        helperText=""
        fullWidth
        multiline
        name="psychiatricHistory.lethalityRisk"
        minRows={4}
      />
      <FastField
        component={CustomTextField}
        label="Psychotic Symptoms (Hallucinations, Delusions, Disorganization)"
        helperText=""
        fullWidth
        multiline
        name="psychiatricHistory.psychoticSymptoms"
        minRows={4}
      />
      <FastField
        component={CustomTextField}
        label="Sleep Patterns and Concerns"
        helperText=""
        fullWidth
        multiline
        name="psychiatricHistory.sleepPatterns"
        minRows={4}
      />
      <FastField
        component={CustomTextField}
        label="Appetite and Nutritional Intake"
        helperText=""
        fullWidth
        multiline
        name="psychiatricHistory.appetiteAndNutrition"
        minRows={4}
      />
      <FastField
        component={CustomTextField}
        label="Sensory Perceptions or Disturbances"
        helperText=""
        fullWidth
        multiline
        name="psychiatricHistory.sensoryPerceptions"
        minRows={4}
      />
      <FastField
        component={CustomTextField}
        label="Attention-Deficit and Hyperactivity Symptoms"
        helperText=""
        fullWidth
        multiline
        name="psychiatricHistory.attentionDeficitSymptoms"
        minRows={4}
      />
    </Section>
    <Section title="Social and Functional History">
      <FastField
        component={CustomTextField}
        label="Education Employment Status"
        helperText=""
        fullWidth
        multiline
        name="socialAndFunctionalHistory.educationEmploymentStatus"
        minRows={4}
      />
      <FastField
        component={CustomTextField}
        label="Substance Use History (Alcohol, Tobacco, Drugs)"
        helperText=""
        fullWidth
        multiline
        name="socialAndFunctionalHistory.substanceUseHistory"
        minRows={4}
      />
      <FastField
        component={CustomTextField}
        label="Medical History (Chronic Illnesses, Surgeries, Relevant Conditions)"
        helperText=""
        fullWidth
        multiline
        name="socialAndFunctionalHistory.medicalHistory"
        minRows={4}
      />
      <FastField
        component={CustomTextField}
        label="Family Psychiatric and Medical History"
        helperText=""
        fullWidth
        multiline
        name="socialAndFunctionalHistory.familyPsychiatricHistory"
        minRows={4}
      />
    </Section>
    <Section title="Summary and Treatment Plan">
      <FastField
        component={CustomTextField}
        label="Therapeutic Interventions"
        helperText=""
        fullWidth
        multiline
        name="therapeuticInterventions"
        minRows={4}
      />
      <FastField
        component={CustomTextField}
        label="Lifestyle and Behavioral Recommendations"
        helperText=""
        fullWidth
        multiline
        name="lifestyleRecommendations"
        minRows={4}
      />
      <FastField
        component={CustomTextField}
        label="Follow-up Plan and Labwork"
        helperText=""
        fullWidth
        multiline
        name="followUpPlan"
        minRows={4}
      />
      <FastField
        component={CustomTextField}
        label="Mental Status Examination"
        helperText="Observations regarding appearance, behavior, speech, mood, affect, thought process, insight, judgment, cognition, and perception"
        fullWidth
        multiline
        name="mentalStatusExamination"
        minRows={4}
      />
      <FastField
        component={CustomTextField}
        label="Clinical Impression"
        helperText="Summary of findings, differential diagnoses, and key clinical considerations"
        fullWidth
        multiline
        name="clinicalImpression"
        minRows={4}
      />
    </Section>
    <Section title="Billing code and diagnosis" mb={6}>
      <BillingCodes name="BILLING_CODES" viewOnly={viewOnly} />
    </Section>
  </>
)

export default IntakeNotePsychiatryFields
